import { Box, HStack } from "@chakra-ui/react";
import { range } from "lodash";
import Select from "components/Select";
import { pluralize } from "helpers/string";
import { combinedOffsetEventOptions } from "features/Trigger/TemplateTrigger/Form/fields";

interface OffsetFieldProps {
  value: number;
  onChange: (value: number) => void;
  referenceEvent: string;
  onReferenceEventChange?: (event: string) => void;
  offsetUnit?: string;
  dayOfWeek?: string;
  onUnitChange?: (unit: string) => void;
  onDayChange?: (day: string | undefined) => void;
  disabled?: boolean;
  errors?: any;
  // Required: drives which timing options render (due fields get "Due Immediately", others "After Kickoff")
  fieldName: string;
}

export default function OffsetField({
  value,
  onChange,
  referenceEvent,
  onReferenceEventChange,
  offsetUnit,
  dayOfWeek,
  onUnitChange,
  onDayChange,
  disabled = false,
  errors,
  fieldName,
}: OffsetFieldProps) {
  // Derive UI state from signed integer offset
  let direction: "exact" | "before" | "after" = "exact";
  if (value > 0) {
    direction = "after";
  } else if (value < 0) {
    direction = "before";
  }
  const absValue = Math.abs(value);

  // Combined select options (direction + reference event)
  const combinedOptions = combinedOffsetEventOptions(fieldName);

  // Find current combined selection
  const currentCombinedValue = combinedOptions.find(
    (option) => option.value.offsetDirection === direction && option.value.referenceEvent === referenceEvent,
  );

  // Handle combined select change
  const handleCombinedChange = (selected: any) => {
    if (selected) {
      const newDirection = selected.value.offsetDirection;
      const newReferenceEvent = selected.value.referenceEvent;

      // Update reference event
      if (onReferenceEventChange) {
        onReferenceEventChange(newReferenceEvent);
      }

      // When switching to/from "exact", handle offset appropriately
      if (newDirection === "exact") {
        onChange(0);
        // Clear offsetUnit when setting offset to 0
        if (onUnitChange) onUnitChange("");
        if (onDayChange) onDayChange(undefined);
      } else {
        // When switching from "exact" to "before/after", default to ±1 day
        const newAbsValue = value === 0 ? 1 : absValue;
        onChange(newDirection === "before" ? -newAbsValue : newAbsValue);
        // Set default unit to "day" if not already set
        if (!offsetUnit && onUnitChange) {
          onUnitChange("day");
        }
      }
    }
  };

  // Offset value options (1-119)
  const offsetValueOptions = range(1, 120).map((val) => ({
    label: val.toString(),
    value: val,
  }));

  // Handle offset value change
  const handleOffsetValueChange = (selected: any) => {
    if (selected) {
      const newAbsValue = selected.value;
      onChange(direction === "before" ? -newAbsValue : newAbsValue);
    }
  };

  // Unit options with weekday shortcuts using option groups
  const getUnitOptions = () => [
    {
      label: "Time Units",
      options: [
        { label: pluralize("Day", absValue), value: "day" },
        { label: pluralize("Week", absValue), value: "week" },
        { label: pluralize("Month", absValue), value: "month" },
      ],
    },
    {
      label: "Specific Days",
      options: [
        { label: pluralize("Monday", absValue), value: "specific_day:monday" },
        { label: pluralize("Tuesday", absValue), value: "specific_day:tuesday" },
        { label: pluralize("Wednesday", absValue), value: "specific_day:wednesday" },
        { label: pluralize("Thursday", absValue), value: "specific_day:thursday" },
        { label: pluralize("Friday", absValue), value: "specific_day:friday" },
      ],
    },
  ];

  // Get current unit value for select
  const getCurrentUnitValue = () => {
    if (offsetUnit === "specific_day" && dayOfWeek) {
      return `specific_day:${dayOfWeek}`;
    }
    return offsetUnit || "";
  };

  // Find the current option across all groups
  const getCurrentUnitOption = () => {
    const currentValue = getCurrentUnitValue();
    const allGroups = getUnitOptions();

    // Flatten all options from all groups and find the matching one
    const found = allGroups
      .flatMap((group) => group.options)
      .find((opt: any) => opt.value === currentValue);

    return found || null;
  };

  // Handle unit change
  const handleUnitChange = (selected: any) => {
    if (selected && selected.value) {
      const selectedValue = selected.value;
      if (selectedValue.startsWith("specific_day:")) {
        const [unit, day] = selectedValue.split(":");
        if (onUnitChange) onUnitChange(unit);
        if (onDayChange) onDayChange(day);
      } else {
        if (onUnitChange) onUnitChange(selectedValue);
        if (onDayChange) onDayChange(undefined);
      }
    }
  };

  return (
    <>
      {/* Combined timing select */}
      <HStack width="100%" mb="1">
        <Box w="100%">
          <Select
            isClearable={false}
            required
            label="Timing"
            options={combinedOptions as any}
            value={currentCombinedValue as any}
            onChange={handleCombinedChange}
            errors={errors}
            isDisabled={disabled}
          />
        </Box>
      </HStack>

      {/* Offset value and unit (only when not exact) */}
      {direction !== "exact" && (
        <HStack width="100%">
          {/* Offset value select */}
          <Box display="inline-block" width="25%">
            <Select
              menuPlacement="top"
              isClearable={false}
              required
              label="Timing"
              placeholder="Number"
              options={offsetValueOptions as any}
              value={offsetValueOptions.find((opt) => opt.value === absValue) as any}
              onChange={handleOffsetValueChange}
              errors={errors}
              isDisabled={disabled}
            />
          </Box>

          {/* Offset unit select */}
          <Box display="inline-block" width="40%">
            <Select
              menuPlacement="top"
              isClearable={false}
              required
              label="Day/Week/Month"
              placeholder="Day/Week/Month"
              options={getUnitOptions() as any}
              value={getCurrentUnitOption() as any}
              onChange={handleUnitChange}
              errors={errors}
              isDisabled={disabled}
            />
          </Box>
        </HStack>
      )}
    </>
  );
}
